import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'access-document',
  version: '1.0.0',
  properties: [
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select Access Document credential',
      credentialTypes: [{ name: 'accessDocument', required: true }],
    },
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'listFolders',
      options: [
        { value: 'downloadDocument', name: 'Download Document' },
        { value: 'getDocument', name: 'Get Document Metadata' },
        { value: 'listDocuments', name: 'List/Search Documents' },
        { value: 'listDocumentTypes', name: 'List Document Types' },
        { value: 'listFolders', name: 'List Folders' },
        { value: 'listTags', name: 'List Tags' },
        { value: 'updateDocument', name: 'Update Document' },
        { value: 'uploadDocument', name: 'Upload Document' },
      ],
    },
    // downloadDocument properties
    {
      key: 'documentId',
      label: 'Document ID',
      type: 'string',
      required: true,
      placeholder: '12345 or {{previousNode.documentId}}',
      description: 'The ID of the document to download, retrieve, or update',
      displayOptions: {
        show: {
          action: ['downloadDocument', 'getDocument', 'updateDocument'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getDocuments',
          dependsOn: ['folderId', 'docTypeId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    {
      key: 'versionId',
      label: 'Version ID',
      type: 'string',
      required: false,
      placeholder: 'Leave empty for latest version',
      description: 'Specific version ID to download (omit for latest)',
      displayOptions: {
        show: {
          action: ['downloadDocument'],
        },
      },
    },
    {
      key: 'decodeTextContent',
      label: 'Decode Text Content',
      type: 'boolean',
      default: false,
      description: 'Automatically decode text files (CSV, JSON, TXT, XML) to readable text. When enabled, returns "content" as text in addition to contentBase64.',
      displayOptions: {
        show: {
          action: ['downloadDocument'],
        },
      },
    },
    {
      key: 'parseJsonContent',
      label: 'Parse JSON Content',
      type: 'boolean',
      default: false,
      description: 'Automatically parse JSON files into an object/array. When enabled, returns "json" as the parsed data. This is faster than using a Code node for large JSON files.',
      displayOptions: {
        show: {
          action: ['downloadDocument'],
          decodeTextContent: [true],
        },
      },
    },
    // listDocuments properties
    {
      key: 'folderId',
      label: 'Folder ID',
      type: 'string',
      required: true,
      placeholder: '10 or {{previousNode.folderId}}',
      description: 'Folder ID to search in',
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    {
      key: 'includeSubFolders',
      label: 'Include Subfolders',
      type: 'boolean',
      required: false,
      default: false,
      description: 'Search in subfolders recursively',
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
    },
    {
      key: 'docTypeId',
      label: 'Document Type ID',
      type: 'string',
      required: true,
      placeholder: '57 or {{previousNode.docTypeId}}',
      description: 'Document type ID',
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDocumentTypes',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'docTypeIdUpload',
      label: 'Document Type ID',
      type: 'string',
      required: true,
      placeholder: '57 or {{previousNode.docTypeId}}',
      description: 'Document type ID for the uploaded document',
      displayOptions: {
        show: {
          action: ['uploadDocument'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDocumentTypes',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'docTypeIdUpdate',
      label: 'Document Type ID',
      type: 'string',
      required: false,
      placeholder: '57 or {{previousNode.docTypeId}}',
      description: 'New document type ID (leave empty to keep current)',
      displayOptions: {
        show: {
          action: ['updateDocument'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDocumentTypes',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'simpleSearch',
      label: 'Simple Search',
      type: 'boolean',
      required: false,
      default: true,
      description: 'Use simple search (true) or advanced metadata search (false)',
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
    },
    {
      key: 'contentSearchPhrase',
      label: 'Search Phrase',
      type: 'string',
      required: false,
      placeholder: 'invoice 2024 or {{previousNode.searchTerm}}',
      description: 'Text to search for in document content/title (supports variable interpolation)',
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
    },
    {
      key: 'skip',
      label: 'Skip',
      type: 'number',
      required: false,
      default: 0,
      placeholder: '0',
      description: 'Number of results to skip (pagination)',
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
    },
    {
      key: 'take',
      label: 'Take',
      type: 'number',
      required: false,
      default: 100,
      placeholder: '100',
      description: 'Maximum results to return (max: 1000)',
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
    },
    {
      key: 'sortField',
      label: 'Sort Field',
      type: 'options',
      required: false,
      default: 'DocTitle',
      options: [
        { value: 'DocTitle', name: 'Document Title' },
        { value: 'AddedOn', name: 'Date Added' },
        { value: 'FileExt', name: 'File Extension' },
      ],
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
    },
    {
      key: 'sortDirection',
      label: 'Sort Direction',
      type: 'options',
      required: false,
      default: 'Ascending',
      options: [
        { value: 'Ascending', name: 'Ascending' },
        { value: 'Descending', name: 'Descending' },
      ],
      displayOptions: {
        show: {
          action: ['listDocuments'],
        },
      },
    },
    // listFolders properties
    {
      key: 'includeDocumentCounts',
      label: 'Include Document Counts',
      type: 'boolean',
      required: false,
      default: false,
      description: 'Include count of documents in each folder',
      displayOptions: {
        show: {
          action: ['listFolders'],
        },
      },
    },
    // updateDocument properties
    {
      key: 'title',
      label: 'Title',
      type: 'string',
      required: false,
      placeholder: 'New Document Title',
      description: 'New document title (leave empty to keep current)',
      displayOptions: {
        show: {
          action: ['updateDocument', 'uploadDocument'],
        },
      },
    },
    {
      key: 'destinationFolderId',
      label: 'Destination Folder ID',
      type: 'string',
      required: false,
      placeholder: '10 or {{previousNode.folderId}}',
      description: 'Folder ID to move/upload document to',
      displayOptions: {
        show: {
          action: ['updateDocument', 'uploadDocument'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    {
      key: 'tagValues',
      label: 'Tag Values',
      type: 'json',
      required: false,
      placeholder: '[{"tagId": 17, "tagValue": "INV-12345"}]',
      description: 'JSON array of tag values for document metadata',
      displayOptions: {
        show: {
          action: ['updateDocument', 'uploadDocument'],
        },
      },
    },
    // uploadDocument properties
    {
      key: 'fileContent',
      label: 'File Content',
      type: 'string',
      required: true,
      placeholder: '{{previousNode.fileContent}}',
      description: 'Base64-encoded file content from previous node (e.g., FTP Download)',
      displayOptions: {
        show: {
          action: ['uploadDocument'],
        },
      },
    },
    {
      key: 'fileName',
      label: 'File Name',
      type: 'string',
      required: true,
      placeholder: 'invoice.pdf or {{previousNode.fileName}}',
      description: 'File name with extension',
      displayOptions: {
        show: {
          action: ['uploadDocument'],
        },
      },
    },
  ],
};

export default schema;
